/**
 * Seasonal Data Utilities for v2v2 SWFL Farmers Market Application
 * Load and parse seasonal produce data for Southwest Florida
 */

import {
  SeasonalData,
  SeasonalProduce,
  MonthAvailability,
  Month,
  Season,
  SEASONS,
  SEASON_LABELS,
  AvailabilityStatus
} from '@/types/seasonal';

// Type definitions for the raw JSON data
interface SeasonalDataRaw {
  seasonalAvailability: { [month: string]: MonthAvailability };
  produceByCategory: { [category: string]: SeasonalProduce[] };
  shoppingTips: Array<{ tip: string; description: string }>;
  whereToShop: {
    farmersMarkets: string[];
    uPickFarms: string[];
    farmStands: string[];
  };
}

// Lazy load and cache the seasonal data
let cachedData: SeasonalData | null = null;
let loadPromise: Promise<SeasonalData> | null = null;

// Default seasonal data for SWFL - used when no JSON files are loaded
const defaultSeasonalData: SeasonalDataRaw = {
  seasonalAvailability: {
    January: {
      peak: ['Strawberries', 'Citrus', 'Lettuce', 'Kale', 'Cabbage'],
      available: ['Tomatoes', 'Peppers', 'Broccoli', 'Cauliflower'],
      limited: ['Corn', 'Watermelon'],
      highlights: 'Peak citrus season in Southwest Florida',
      farmersMarketTips: 'Look for fresh-picked strawberries and naval oranges',
    },
    February: {
      peak: ['Strawberries', 'Citrus', 'Lettuce', 'Spinach'],
      available: ['Tomatoes', 'Peppers', 'Broccoli'],
      limited: ['Corn', 'Melons'],
      highlights: 'Last month for peak strawberry season',
      farmersMarketTips: 'Great time for citrus varieties',
    },
    March: {
      peak: ['Tomatoes', 'Strawberries', 'Lettuce'],
      available: ['Peppers', 'Cucumbers', 'Squash'],
      limited: ['Corn'],
      highlights: 'Spring vegetables arriving',
      farmersMarketTips: 'Transition from winter to spring crops',
    },
    April: {
      peak: ['Tomatoes', 'Peppers', 'Cucumbers'],
      available: ['Squash', 'Eggplant', 'Beans'],
      limited: ['Strawberries'],
      highlights: 'Spring harvest in full swing',
      farmersMarketTips: 'Best time for vine-ripened tomatoes',
    },
    May: {
      peak: ['Mangoes', 'Tomatoes', 'Peppers'],
      available: ['Squash', 'Eggplant', 'Okra'],
      limited: ['Leafy Greens'],
      highlights: 'Mango season begins',
      farmersMarketTips: 'Try local mango varieties',
    },
    June: {
      peak: ['Mangoes', 'Avocados', 'Lychee'],
      available: ['Peppers', 'Okra', 'Southern Peas'],
      limited: ['Tomatoes'],
      highlights: 'Tropical fruit season',
      farmersMarketTips: 'Seek out tropical fruits',
    },
    July: {
      peak: ['Mangoes', 'Avocados'],
      available: ['Okra', 'Southern Peas', 'Sweet Potatoes'],
      limited: ['Most vegetables'],
      highlights: 'Peak mango season',
      farmersMarketTips: 'Limited local produce due to heat',
    },
    August: {
      peak: ['Avocados'],
      available: ['Okra', 'Sweet Potatoes'],
      limited: ['Most vegetables'],
      highlights: 'Late summer avocados',
      farmersMarketTips: 'Quietest month for local produce',
    },
    September: {
      peak: ['Avocados', 'Papayas'],
      available: ['Okra', 'Sweet Potatoes', 'Peppers'],
      limited: ['Tomatoes'],
      highlights: 'Fall planting begins',
      farmersMarketTips: 'Fall crops starting to appear',
    },
    October: {
      peak: ['Peppers', 'Squash'],
      available: ['Tomatoes', 'Eggplant', 'Beans'],
      limited: ['Tropical Fruits'],
      highlights: 'Fall harvest begins',
      farmersMarketTips: 'Great variety returning',
    },
    November: {
      peak: ['Citrus', 'Lettuce', 'Broccoli'],
      available: ['Tomatoes', 'Peppers', 'Cauliflower'],
      limited: ['Mangoes'],
      highlights: 'Citrus season starts',
      farmersMarketTips: 'Early citrus varieties available',
    },
    December: {
      peak: ['Citrus', 'Strawberries', 'Lettuce', 'Kale'],
      available: ['Tomatoes', 'Peppers', 'Root Vegetables'],
      limited: ['Tropical Fruits'],
      highlights: 'Peak winter produce season',
      farmersMarketTips: 'Best selection of the year',
    },
  },
  produceByCategory: {
    fruits: [
      { name: 'Strawberries', season: 'December-March', peak: 'January-February', description: 'Sweet Florida strawberries', uPickLocations: ['Fancy Farms', 'Bedners Farm'] },
      { name: 'Mangoes', season: 'May-September', peak: 'June-August', description: 'Tropical mangoes', uPickLocations: ['Pine Island Tropicals'] },
      { name: 'Citrus', season: 'November-April', peak: 'December-February', description: 'Oranges, grapefruits, tangerines', uPickLocations: ['Orange River Groves'] },
      { name: 'Avocados', season: 'June-October', peak: 'July-September', description: 'Florida avocados', uPickLocations: [] },
    ],
    vegetables: [
      { name: 'Tomatoes', season: 'October-June', peak: 'January-March', description: 'Vine-ripened tomatoes', uPickLocations: [] },
      { name: 'Peppers', season: 'October-May', peak: 'December-February', description: 'Bell and hot peppers', uPickLocations: [] },
      { name: 'Lettuce', season: 'November-April', peak: 'January-February', description: 'Fresh leafy greens', uPickLocations: [] },
      { name: 'Squash', season: 'October-May', peak: 'November-January', description: 'Summer and winter squash', uPickLocations: [] },
    ],
  },
  shoppingTips: [
    { tip: 'Arrive Early', description: 'The best selection is available when markets open' },
    { tip: 'Bring Cash', description: 'Many vendors prefer cash transactions' },
    { tip: 'Ask Questions', description: 'Farmers love to share growing tips and recipes' },
  ],
  whereToShop: {
    farmersMarkets: ['Cape Coral Farmers Market', 'Fort Myers Downtown Farmers Market', 'Naples Farmers Market'],
    uPickFarms: ['Fancy Farms', 'Bedners Farm', 'Pine Island Tropicals'],
    farmStands: ['Farmers Market Restaurant', 'Sun Harvest Citrus'],
  },
};

/**
 * Load the seasonal data - uses static default data for CRA compatibility
 */
async function loadSeasonalDataAsync(): Promise<SeasonalData> {
  if (cachedData) {
    return cachedData;
  }

  // Use static default data (CRA doesn't support import.meta.glob)
  const raw = defaultSeasonalData;

  cachedData = {
    currentMonth: getCurrentMonth(),
    months: raw.seasonalAvailability,
    produceByCategory: raw.produceByCategory,
    shoppingTips: raw.shoppingTips,
    whereToShop: raw.whereToShop,
  };

  return cachedData;
}

/**
 * Get the cached seasonal data (async)
 */
export async function getSeasonalData(): Promise<SeasonalData> {
  if (cachedData) {
    return cachedData;
  }
  
  if (!loadPromise) {
    loadPromise = loadSeasonalDataAsync();
  }
  
  return loadPromise;
}

/**
 * Synchronous access to cached data (returns null if not loaded)
 */
export function getSeasonalDataSync(): SeasonalData | null {
  return cachedData;
}

/**
 * Clear the cached data (useful for testing)
 */
export function clearSeasonalDataCache(): void {
  cachedData = null;
  loadPromise = null;
}

/**
 * Get the current month name
 */
export function getCurrentMonth(): Month {
  const now = new Date();
  const monthIndex = now.getMonth(); // 0-11
  const months: Month[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[monthIndex];
}

/**
 * Get the current season based on current month
 */
export function getCurrentSeason(): Season {
  const currentMonth = getCurrentMonth();
  return getSeasonFromMonth(currentMonth);
}

/**
 * Get season from month name
 */
export function getSeasonFromMonth(month: Month): Season {
  if ((SEASONS.spring as readonly Month[]).includes(month)) return 'spring';
  if ((SEASONS.summer as readonly Month[]).includes(month)) return 'summer';
  if ((SEASONS.fall as readonly Month[]).includes(month)) return 'fall';
  return 'winter';
}

/**
 * Get season display label
 */
export function getSeasonLabel(season: Season): string {
  return SEASON_LABELS[season];
}

/**
 * Get produce availability for a specific month
 */
export function getAvailabilityForMonth(
  month: Month,
  data: SeasonalData
): MonthAvailability | undefined {
  return data.months[month];
}

/**
 * Get produce availability status for a specific produce item
 */
export function getProduceAvailabilityStatus(
  produceName: string,
  month: Month,
  data: SeasonalData
): AvailabilityStatus {
  const availability = data.months[month];
  
  if (!availability) return 'limited';

  const normalizedName = produceName.toLowerCase();

  // Check peak list first
  if (availability.peak.some((p: string) => p.toLowerCase() === normalizedName)) {
    return 'peak';
  }

  // Check available list
  if (availability.available.some((a: string) => a.toLowerCase() === normalizedName)) {
    return 'available';
  }

  // Check limited list
  if (availability.limited.some((l: string) => l.toLowerCase() === normalizedName)) {
    return 'limited';
  }

  return 'limited';
}

/**
 * Check if specific produce is in season for current month
 */
export function isInSeason(produceName: string, data: SeasonalData): boolean {
  const currentMonth = getCurrentMonth();
  const status = getProduceAvailabilityStatus(produceName, currentMonth, data);
  return status !== 'limited';
}

/**
 * Check if produce is year-round
 */
export function isYearRound(produceName: string, data: SeasonalData): boolean {
  const yearRoundKeywords = ['year-round', 'year round', '12 months'];
  
  // Check all categories for the produce
  for (const category of Object.values(data.produceByCategory)) {
    for (const produce of category) {
      if (produce.name.toLowerCase() === produceName.toLowerCase()) {
        return yearRoundKeywords.some(kw => 
          produce.season.toLowerCase().includes(kw)
        );
      }
    }
  }
  return false;
}

/**
 * Get produce by category
 */
export function getProduceByCategory(
  category: string,
  data: SeasonalData
): SeasonalProduce[] {
  return data.produceByCategory[category] || [];
}

/**
 * Get all categories
 */
export function getAllCategories(data: SeasonalData): string[] {
  return Object.keys(data.produceByCategory);
}

/**
 * Get all produce items
 */
export function getAllProduce(data: SeasonalData): SeasonalProduce[] {
  const allProduce: SeasonalProduce[] = [];
  
  for (const category of Object.values(data.produceByCategory)) {
    allProduce.push(...category);
  }
  return allProduce;
}

/**
 * Get peak produce for current month
 */
export function getPeakProduce(data: SeasonalData): string[] {
  return data.months[data.currentMonth]?.peak || [];
}

/**
 * Get produce by month name
 */
export function getProduceByMonth(
  month: Month,
  status: 'peak' | 'available' | 'limited' = 'peak',
  data?: SeasonalData
): string[] {
  const seasonalData = data || cachedData;
  if (!seasonalData) return [];
  
  const availability = seasonalData.months[month];
  if (!availability) return [];
  return availability[status];
}

/**
 * Get U-Pick locations for specific produce
 */
export function getUPickLocations(
  produceName: string,
  data: SeasonalData
): string[] {
  const normalizedName = produceName.toLowerCase();
  
  // Search through all categories
  for (const category of Object.values(data.produceByCategory)) {
    for (const produce of category) {
      if (produce.name.toLowerCase() === normalizedName) {
        return produce.uPickLocations || [];
      }
    }
  }
  
  return [];
}

/**
 * Get all U-Pick farms
 */
export function getAllUPickFarms(data: SeasonalData): string[] {
  return data.whereToShop.uPickFarms;
}

/**
 * Get all farmers markets
 */
export function getAllFarmersMarkets(data: SeasonalData): string[] {
  return data.whereToShop.farmersMarkets;
}

/**
 * Get shopping tips
 */
export function getShoppingTips(data: SeasonalData): Array<{ tip: string; description: string }> {
  return data.shoppingTips;
}

/**
 * Format seasonal data for UI - get current month data
 */
export function formatCurrentMonthData(data: SeasonalData) {
  const month = data.currentMonth;
  const availability = data.months[month];
  const season = getSeasonFromMonth(month);
  
  return {
    month,
    season,
    seasonLabel: getSeasonLabel(season),
    peakProduce: availability?.peak || [],
    availableProduce: availability?.available || [],
    limitedProduce: availability?.limited || [],
    highlights: availability?.highlights || '',
    tips: availability?.farmersMarketTips || '',
    totalPeak: availability?.peak.length || 0,
    totalAvailable: (availability?.available.length || 0) + (availability?.peak.length || 0),
  };
}

/**
 * Get seasonal highlights for sidebar/footer
 */
export function getSeasonalHighlights(
  limit: number = 5,
  data: SeasonalData
): string[] {
  return data.months[data.currentMonth]?.peak.slice(0, limit) || [];
}

/**
 * Find produce by name across all categories
 */
export function findProduceByName(
  name: string,
  data: SeasonalData
): SeasonalProduce | undefined {
  const normalizedName = name.toLowerCase();
  
  for (const category of Object.values(data.produceByCategory)) {
    for (const produce of category) {
      if (produce.name.toLowerCase() === normalizedName) {
        return produce;
      }
    }
  }
  return undefined;
}

/**
 * Search produce by keyword
 */
export function searchProduceByKeyword(
  keyword: string,
  data: SeasonalData
): SeasonalProduce[] {
  const normalizedKeyword = keyword.toLowerCase();
  const results: SeasonalProduce[] = [];
  
  for (const category of Object.values(data.produceByCategory)) {
    for (const produce of category) {
      if (
        produce.name.toLowerCase().includes(normalizedKeyword) ||
        produce.description.toLowerCase().includes(normalizedKeyword) ||
        produce.season.toLowerCase().includes(normalizedKeyword)
      ) {
        results.push(produce);
      }
    }
  }
  return results;
}

/**
 * Get produce that is currently in peak season
 */
export function getCurrentlyPeakSeasonProduce(data: SeasonalData): SeasonalProduce[] {
  const currentMonth = data.currentMonth;
  const peakProduceNames = data.months[currentMonth]?.peak || [];
  
  const allProduce = getAllProduce(data);
  
  return allProduce.filter(produce => 
    peakProduceNames.some((name: string) => 
      name.toLowerCase() === produce.name.toLowerCase()
    )
  );
}

/**
 * Get category display name (convert snake_case to Title Case)
 */
export function getCategoryDisplayName(categoryKey: string): string {
  return categoryKey
    .split(/(?=[A-Z])/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get month abbreviation
 */
export function getMonthAbbreviation(month: string): string {
  return month.slice(0, 3);
}

/**
 * Get all months in order
 */
export function getAllMonths(): Month[] {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
}
